/**
 * Convert image file to WebP format and then to base64
 * @param {File} file - The image file to convert
 * @param {Object} options - Conversion options
 * @param {number} options.quality - WebP quality (0-100), default 85
 * @param {number} options.maxWidth - Maximum width, default 1920
 * @param {number} options.maxHeight - Maximum height, default 1920
 * @returns {Promise<string>} - Base64 string of the converted WebP image
 */
export const convertImageToWebPBase64 = (file, options = {}) => {
  const {
    quality = 85,
    maxWidth = 1920,
    maxHeight = 1920,
  } = options;

  return new Promise((resolve, reject) => {
    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      reject(new Error('File must be an image'));
      return;
    }

    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target.result;
    };

    img.onload = () => {
      // Calculate dimensions (maintain aspect ratio)
      let width = img.width;
      let height = img.height;

      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = width * ratio;
        height = height * ratio;
      }

      // Create canvas for conversion
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');

      // Enable image smoothing for better quality
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Draw image on canvas
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to WebP base64
      try {
        const webpDataUrl = canvas.toDataURL('image/webp', quality / 100);

        // Get the base64 data (remove the data:image/webp;base64, prefix)
        const base64Data = webpDataUrl.split(',')[1];

        resolve({
          base64: base64Data,
          dataUrl: webpDataUrl,
          width,
          height,
          size: Math.round((webpDataUrl.length * 3) / 4), // Approximate size in bytes
          format: 'webp',
        });
      } catch (error) {
        reject(new Error('Failed to convert image to WebP: ' + error.message));
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Convert image file to base64 without WebP conversion (fallback)
 * @param {File} file - The image file to convert
 * @param {Object} options - Conversion options
 * @param {number} options.maxWidth - Maximum width, default 1920
 * @param {number} options.maxHeight - Maximum height, default 1920
 * @returns {Promise<string>} - Base64 string of the converted image
 */
export const convertImageToBase64 = (file, options = {}) => {
  const {
    maxWidth = 1920,
    maxHeight = 1920,
  } = options;

  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('File must be an image'));
      return;
    }

    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target.result;
    };

    img.onload = () => {
      let width = img.width;
      let height = img.height;

      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = width * ratio;
        height = height * ratio;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      ctx.drawImage(img, 0, 0, width, height);

      try {
        const dataUrl = canvas.toDataURL(file.type, 0.85);
        const base64Data = dataUrl.split(',')[1];

        resolve({
          base64: base64Data,
          dataUrl,
          width,
          height,
          size: Math.round((dataUrl.length * 3) / 4),
          format: file.type.split('/')[1],
        });
      } catch (error) {
        reject(new Error('Failed to convert image: ' + error.message));
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Get image info before conversion
 * @param {File} file - The image file
 * @returns {Promise<Object>} - Image information
 */
export const getImageInfo = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target.result;
    };

    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height,
        type: file.type,
        size: file.size,
        name: file.name,
      });
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Format file size to human readable format
 * @param {number} bytes - Size in bytes
 * @returns {string} - Formatted size string
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Validate image file
 * @param {File} file - The file to validate
 * @param {Object} rules - Validation rules
 * @returns {Object} - Validation result { valid: boolean, error: string }
 */
export const validateImageFile = (file, rules = {}) => {
  const {
    maxSize = 5 * 1024 * 1024, // 5MB default
    allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'],
  } = rules;

  if (!file) {
    return { valid: false, error: 'No file provided' };
  }

  if (!allowedTypes.includes(file.type)) {
    return {
      valid: false,
      error: `Invalid file type. Allowed: ${allowedTypes.join(', ')}`,
    };
  }

  if (file.size > maxSize) {
    return {
      valid: false,
      error: `File too large. Maximum size: ${formatFileSize(maxSize)}`,
    };
  }

  return { valid: true };
};
